import { getAllIds, getCountByStatus, useGetAllAuctions } from "@/api/auction/auction";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewAuctionDialog from "@/components/auction/NewAuctionDialog";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DashboardListGridLayout } from "@/components/dashboard/DashboardListGridLayout";
import { GridCardWithActions } from "@/components/links/GridCardWithActions";
import { AuctionLinkGridCard } from "@/components/links/relatedLinkGridCards";
import { DashboardGridCardActions } from "@/components/table/DashboardGridCardActions";
import { DataTable } from "@/components/ui/datatable";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  publicAuctionAdvancedSearchConfig,
  publicAuctionColumns,
} from "@/config/dashboard/public-auction";
import DashboardSelectionBar from "@/components/table/DashboardSelectionBar";
import { useDashboardListScrollContext } from "@/hooks/useDashboardListScrollContext";
import { useDashboardActionsColumn } from "@/hooks/useDashboardActionsColumn";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useDashboardRowSelection } from "@/hooks/useDashboardRowSelection";
import { Auction } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIONS_COLUMN_ID,
  ensureActionsColumnLast,
} from "@/components/table/table-actions-column";

const STORAGE_KEY = "auction-table-preferences";
const VIGNETTE_KEY = "ventesDescriptionVignette";

const flatVisibleAuctionColumns = [
  ...publicAuctionColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const ensureVignetteSecond = (order: string[]): string[] => {
  const without = order.filter((k) => k !== VIGNETTE_KEY);
  if (without.length === 0) return order;
  return [without[0], VIGNETTE_KEY, ...without.slice(1)];
};

const AuctionDashboard = () => {
  const { t } = useTranslation();
  const {
    countByStatus,
    fetchParams,
    filterStatus,
    setFilterStatus,
    pagination,
    setPagination,
    currentCount,
  } = useDashboardParams({
    resourceName: "auction",
    resourceCounter: getCountByStatus,
    statusField: "ventesPublicationStatus",
  });

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    const order = saved
      ? saved.order || flatVisibleAuctionColumns
      : flatVisibleAuctionColumns;
    return ensureVignetteSecond(ensureActionsColumnLast(order));
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...publicAuctionColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...publicAuctionColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  const { data, isFetching } = useGetAllAuctions(fetchParams);
  const {
    listKey,
    navigateToFiche,
    entityIds,
    isLoading: listScrollLoading,
    listPagination,
  } = useDashboardListScrollContext(data, pagination, isFetching, STORAGE_KEY);

  const actionsColumn = useDashboardActionsColumn<Auction>({
    documentType: "auction",
    publicationStatusField: "ventesPublicationStatus",
    onEdit: (row) =>
      navigateToFiche(`/edit/auction/${row.id}`, row.id),
  });

  const columns: ColumnDef<Auction, any>[] = useMemo(() => {
    return [...generateTable(publicAuctionColumns, "auction"), actionsColumn];
  }, [actionsColumn]);


  const {
    selectionState,
    setSelectionState,
    dataTableSelection,
    isSelected,
    setRowSelected,
  } = useDashboardRowSelection<Auction>({
      getAllRowIds: () => getAllIds(fetchParams.where),
    });

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("dashboardTitleAuctions")}</p>
        <GlobalSearch resource="auction"
          config={publicAuctionAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          onValueChange={setFilterStatus}
          countByStatus={countByStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={publicAuctionAdvancedSearchConfig}
            resource="auction"
          />
          <AdvancedSort config={publicAuctionAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createAauction")}
            Dialog={NewAuctionDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DashboardListGridLayout
          storageKey={STORAGE_KEY}
          listKey={listKey}
          entityIds={entityIds}
          isLoading={listScrollLoading}
          listPagination={listPagination}
          setListPagination={setPagination}
          listTable={
            <DataTable
              onRowClick={(row) =>
                navigateToFiche(`/edit/auction/${row.original.id}`, row.original.id)
              }
              getListItemId={(row) => row.original.id}
              pagination={{
                pagination: pagination,
                setPage: setPagination,
                totalSize: currentCount,
              }}
              columns={columns}
              data={data ?? []}
              storageKey={STORAGE_KEY}
              options={{
                enableColumnResizing: true,
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
              }}
              selection={dataTableSelection}
            />
          }
          gridContent={
            <>
              {(data ?? []).map((item) => (
                <GridCardWithActions
                  key={item.id}
                  selected={isSelected(item.id)}
                  onSelectedChange={(checked) =>
                    setRowSelected(item.id, checked)
                  }
                  actions={
                    <DashboardGridCardActions
                      documentType="auction"
                      item={item}
                      publicationStatusField="ventesPublicationStatus"
                      onEdit={(row) =>
                        navigateToFiche(`/edit/auction/${row.id}`, row.id)
                      }
                    />
                  }
                >
                  <AuctionLinkGridCard
                    item={item}
                    t={t}
                    onTextClick={() =>
                      navigateToFiche(`/edit/auction/${item.id}`, item.id)
                    }
                  />
                </GridCardWithActions>
              ))}
            </>
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
        />
        <DashboardSelectionBar
          documentType="auction"
          selectionState={selectionState}
          setSelectionState={setSelectionState}
        />
      </div>
    </div>
  );
};

export default AuctionDashboard;
